import type { CompetenceRecord, Sector } from "../../api/client";
import { Callout } from "../../components/Callout";
import { DateField } from "../../components/DateField";
import { MultiSelect } from "../../components/MultiSelect";
import { SelectField } from "../../components/SelectField";
import { claimFor } from "../record";

const LEVELS = ["Lead auditor", "Auditor", "Technical expert"] as const;

/**
 * Screen 14 — competence, standard by sector.
 *
 * Cl. 7.1.2 requires competence criteria per standard **and** per technical area, which is
 * why the sectors sit inside each claim rather than in a list beside them. Competence in
 * ISO 9001 for manufacturing is not competence in ISO 9001 for food processing, and Complao
 * only offers an auditor for audits matching both.
 *
 * Every standard Complao audits is offered, not only the ones the certification body
 * proposed and not only the ones it is accredited for. The proposal is badged and nothing
 * more: an auditor may legitimately hold competence their body is not accredited for, and
 * the dictionary's rule is that a sector outside that scope is **dropped at verification,
 * not rejected here**. Refusing the claim would destroy a true statement about a person.
 */
export function CompetenceStep({
  record,
  standards,
  sectors,
  proposedStandards,
  onToggle,
  onSet,
  onToggleSector,
  onSetSectors,
  errorFor,
}: {
  record: CompetenceRecord;
  standards: readonly string[];
  sectors: readonly Sector[];
  proposedStandards: readonly string[];
  onToggle: (standard: string) => void;
  onSet: (standard: string, patch: Record<string, unknown>) => void;
  onToggleSector: (standard: string, sector: number) => void;
  onSetSectors: (standard: string, sectors: readonly number[]) => void;
  errorFor: (field: string) => string | undefined;
}) {
  const indexOf = (standard: string) =>
    (record.competence ?? []).findIndex((entry) => entry.standard === standard);

  return (
    <div className="card">
      <h2>Competence — standard by sector</h2>

      <Callout title="Competence is not a list of standards.">
        Cl.&nbsp;7.1.2 requires competence criteria per standard <i>and</i> per technical
        area. Being competent in ISO 9001 for manufacturing does not make you competent for
        food processing. Complao will only offer you for audits that match both.
      </Callout>

      {errorFor("competence") ? (
        <p className="err" role="alert">
          {errorFor("competence")}
        </p>
      ) : null}

      {standards.map((standard) => {
        const claim = claimFor(record, standard);
        const at = indexOf(standard);
        const field = (name: string) => errorFor(`competence.${at}.${name}`);

        return (
          <div key={standard} className="plan-ed" style={{ marginBottom: "12px" }}>
            <div
              className="row"
              style={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <label
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                <input
                  type="checkbox"
                  checked={claim !== null}
                  onChange={() => onToggle(standard)}
                />{" "}
                {standard}
              </label>
              {proposedStandards.includes(standard) ? (
                <span className="pill p-wait">Proposed by the CB</span>
              ) : null}
            </div>

            {claim ? (
              <>
                <div className="grid3" style={{ marginTop: "10px" }}>
                  <SelectField
                    id={`competence-${standard}-level`}
                    label="Level"
                    value={claim.level ?? ""}
                    onChange={(level) => onSet(standard, { level })}
                    options={LEVELS.map((value) => ({ value, label: value }))}
                    error={field("level")}
                  />
                  <DateField
                    id={`competence-${standard}-granted`}
                    label="Competence granted"
                    value={claim.granted_on ?? ""}
                    onChange={(granted_on) => onSet(standard, { granted_on })}
                    error={field("granted_on")}
                  />
                  <DateField
                    id={`competence-${standard}-review`}
                    label="Next review"
                    value={claim.next_review ?? ""}
                    onChange={(next_review) => onSet(standard, { next_review })}
                    error={field("next_review")}
                  />
                </div>

                <div style={{ marginTop: "10px" }}>
                  <div className="eyebrow">Technical sectors</div>
                  <div style={{ marginTop: "5px" }}>
                    <MultiSelect
                      id={`competence-${standard}-sectors`}
                      options={sectors.map((sector) => ({
                        value: sector.index,
                        label: `IAF ${String(sector.index).padStart(2, "0")} · ${sector.label}`,
                      }))}
                      selected={claim.sectors ?? []}
                      onToggle={(sector) => onToggleSector(standard, sector)}
                      onSetAll={(sectors) => onSetSectors(standard, sectors)}
                      placeholder="Select the IAF technical sectors you are competent in"
                      searchLabel="Filter sectors"
                      invalid={Boolean(field("sectors"))}
                    />
                  </div>
                  {field("sectors") ? (
                    <p className="err" role="alert">
                      {field("sectors")}
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
